import React, { useState, useCallback } from 'react';
import { Alert, Box, Button, FileUpload, Modal, SpaceBetween, StatusIndicator } from '@amzn/awsui-components-core';
import { FileUploadState } from '../../hooks/useWorkDocsUpload';
import { UPLOAD_ACCEPTED_FILE_TYPES, UPLOAD_MAX_DOCS_PER_FOLDER, UPLOAD_MAX_FILE_SIZE_MB } from '../../constants/BaseballCardConstants';

export interface FolderUploadModalProps {
  /** Whether the modal is open. When null-target the modal is closed by the parent. */
  visible: boolean;
  /** WorkDocs folder id the selected files upload into. */
  folderId: string;
  /** Human-readable folder name shown in the modal header. */
  folderName: string;
  /** Whether an upload is currently in flight (disables the control). */
  isUploading: boolean;
  /** Documents already in this folder — counted against the per-folder document limit. */
  existingFileCount: number;
  /**
   * Max documents allowed in this folder (existing + newly selected). Defaults to the standard
   * per-folder Converse limit; the "Manager Hub Exports" folder passes 1 (single ratings export).
   */
  maxDocs?: number;
  /** This folder's per-file upload status (from statusByFolder.get(folderId)). */
  statusByFile: Map<string, FileUploadState> | undefined;
  /** Uploads the given files into `folderId`. */
  onUpload: (folderId: string, files: File[]) => void;
  onDismiss: () => void;
}

/** Maps a per-file upload status to a Cloudscape StatusIndicator type. */
const statusTypeFor = (status: FileUploadState['status']): 'in-progress' | 'success' | 'error' =>
  status === 'success' ? 'success' : status === 'error' ? 'error' : 'in-progress';

/**
 * Modal upload control scoped to a single WorkDocs folder: a Cloudscape FileUpload to pick
 * files, an "Upload" action, and a per-file status list that reads ONLY this folder's status
 * map — so messages never leak across folders. Upload side-effects live in the parent's
 * {@link useWorkDocsUpload} hook; on success the parent refreshes the listing and clears the
 * succeeded entries (errors are kept).
 */
export const FolderUploadModal: React.FC<FolderUploadModalProps> = ({
  visible,
  folderId,
  folderName,
  isUploading,
  existingFileCount,
  maxDocs = UPLOAD_MAX_DOCS_PER_FOLDER,
  statusByFile,
  onUpload,
  onDismiss
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  // The generation Lambda sends every file in a folder to the Bedrock Converse API, which caps
  // documents per request — so block an upload that would push this folder's total (already
  // uploaded + newly selected) over `maxDocs` (the default per-folder limit, or 1 for the
  // single-export Manager Hub Exports folder).
  const remainingSlots = Math.max(0, maxDocs - existingFileCount);
  const wouldExceedLimit = selectedFiles.length > remainingSlots;
  // A limit of 1 means a single-file folder: constrain the picker to one file and use singular
  // copy so the constraint/error text reads naturally ("1 document", not "1 documents").
  const isSingleFile = maxDocs === 1;
  const docWord = maxDocs === 1 ? 'document' : 'documents';

  const handleUpload = useCallback(() => {
    if (selectedFiles.length === 0 || wouldExceedLimit) return;
    onUpload(folderId, selectedFiles);
    setSelectedFiles([]);
  }, [folderId, selectedFiles, wouldExceedLimit, onUpload]);

  const handleDismiss = useCallback(() => {
    setSelectedFiles([]);
    onDismiss();
  }, [onDismiss]);

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header={`Upload to ${folderName}`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDismiss}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={selectedFiles.length === 0 || isUploading || wouldExceedLimit}
              loading={isUploading}
            >
              Upload
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="s">
        <FileUpload
          multiple={!isSingleFile}
          value={selectedFiles}
          onChange={({ detail }) => setSelectedFiles(detail.value)}
          accept={UPLOAD_ACCEPTED_FILE_TYPES}
          showFileSize
          showFileLastModified
          constraintText={
            isSingleFile
              ? `Only 1 ${docWord} allowed in this folder • Maximum File Size: ${UPLOAD_MAX_FILE_SIZE_MB} MB`
              : `Up to ${maxDocs} ${docWord} per folder (Converse API limit) • Maximum File Size: ${UPLOAD_MAX_FILE_SIZE_MB} MB Per File`
          }
          i18nStrings={{
            uploadButtonText: (multiple) => (multiple ? 'Choose Files' : 'Choose File'),
            dropzoneText: (multiple) => (multiple ? 'Drop Files to Upload' : 'Drop File to Upload'),
            removeFileAriaLabel: (fileIndex) => `Remove File ${fileIndex + 1}`,
            limitShowFewer: 'Show Fewer Files',
            limitShowMore: 'Show More Files',
            errorIconAriaLabel: 'Error'
          }}
        />

        {wouldExceedLimit && (
          <Alert type="error">
            {existingFileCount >= maxDocs
              ? `This folder already has ${existingFileCount} of ${maxDocs} ${docWord}. Remove ${
                  maxDocs === 1 ? 'it' : 'some'
                } before uploading more.`
              : `This folder allows up to ${maxDocs} ${docWord} (${existingFileCount} already uploaded). Select at most ${remainingSlots} more.`}
          </Alert>
        )}

        {statusByFile && statusByFile.size > 0 && (
          <SpaceBetween size="xxs">
            {Array.from(statusByFile.entries()).map(([fileName, state]) => (
              <StatusIndicator key={fileName} type={statusTypeFor(state.status)}>
                {`${fileName}${state.error ? ` — ${state.error}` : ''}`}
              </StatusIndicator>
            ))}
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Modal>
  );
};
